/**
 * useSearchIndex - MiniSearch + Pinyin fuzzy search hook
 *
 * Builds a unified search index over articles, highlights, and cards
 * from Legend-State observables. Supports CJK Pinyin matching.
 *
 * Architecture note: This hook is the "brain" of the search modal.
 * cmdk handles UI/keyboard; this hook handles scoring/ranking.
 * Designed to be reusable if we ever need search outside the modal.
 */

import { useMemo, useCallback } from 'react';
import { useSelector } from '@legendapp/state/react';
import MiniSearch from 'minisearch';
import pinyinMatch from 'pinyin-match';
import { articles$ } from '../store/articles$';
import { getAllHighlights, getCardsArray } from '../store';

/**
 * Get all articles as a flat array (including archived, excluding deleted).
 * We bypass getArticlesArray() because it filters by list/archive status,
 * but search should find everything.
 */
const getAllArticlesForSearch = () => {
  const articlesObj = articles$.get() || {};
  return Object.values(articlesObj)
    .filter(a => a && a.id && !a.deleted);
};

// MiniSearch configuration
const MINISEARCH_OPTIONS = {
  fields: ['title', 'excerpt', 'site_name', 'tags_text', 'exact_text', 'front', 'back'],
  storeFields: [
    '_type', 'originalId',
    // Article fields
    'title', 'excerpt', 'site_name', 'tags', 'cover_url', 'is_starred', 'status', 'last_viewed_at', 'created_at',
    // Highlight fields
    'exact_text', 'article_id',
    // Card fields
    'front', 'back', 'deck_id', 'source_article_id', 'source_highlight_id',
  ],
  searchOptions: {
    prefix: true,
    fuzzy: 0.2,
    boost: {
      title: 5,
      front: 4,
      exact_text: 3,
      excerpt: 2,
      site_name: 1.5,
      tags_text: 1.5,
      back: 1,
    },
  },
};

/**
 * Check if a query looks like a pinyin input (all ASCII letters).
 * Used to decide whether to run pinyin augmentation.
 */
const isPinyinLikeQuery = (query) => /^[a-zA-Z]+$/.test(query);

/**
 * Run pinyin matching against a text string.
 * Returns a score (0 = no match, 5 = match found).
 */
const getPinyinScore = (text, query) => {
  if (!text) return 0;
  try {
    const match = pinyinMatch.match(text, query);
    return match ? 5 : 0;
  } catch {
    return 0;
  }
};

const useSearchIndex = () => {
  // Reactive data sources — triggers re-render when data changes
  const articles = useSelector(() => getAllArticlesForSearch());
  const highlights = useSelector(() => getAllHighlights());
  const cards = useSelector(() => getCardsArray());

  // Build unified MiniSearch index
  const searchIndex = useMemo(() => {
    const index = new MiniSearch(MINISEARCH_OPTIONS);
    const docs = [];

    // Index articles
    articles.forEach(a => {
      docs.push({
        id: `article:${a.id}`,
        _type: 'article',
        originalId: a.id,
        title: a.title || '',
        excerpt: a.excerpt || '',
        site_name: a.site_name || '',
        tags_text: a.tags?.join(' ') || '',
        tags: a.tags,
        cover_url: a.cover_url,
        is_starred: a.is_starred,
        status: a.status,
        last_viewed_at: a.last_viewed_at,
        created_at: a.created_at,
        // Empty fields for other types (MiniSearch needs consistent schema)
        exact_text: '',
        front: '',
        back: '',
        article_id: null,
        deck_id: null,
        source_article_id: null,
      });
    });

    // Index highlights (skip entries without id — can occur during cleanup)
    highlights.filter(h => h.id).forEach(h => {
      docs.push({
        id: `highlight:${h.id}`,
        _type: 'highlight',
        originalId: h.id,
        exact_text: h.exact_text || '',
        article_id: h.article_id,
        created_at: h.created_at,
        // Empty fields for other types
        title: '',
        excerpt: '',
        site_name: '',
        tags_text: '',
        tags: null,
        cover_url: null,
        is_starred: false,
        status: null,
        last_viewed_at: null,
        front: '',
        back: '',
        deck_id: null,
        source_article_id: null,
      });
    });

    // Index cards
    cards.forEach(c => {
      docs.push({
        id: `card:${c.id}`,
        _type: 'card',
        originalId: c.id,
        front: c.front || '',
        back: c.back || '',
        deck_id: c.deck_id,
        source_article_id: c.source_article_id,
        source_highlight_id: c.source_highlight_id || null,
        created_at: c.created_at,
        // Empty fields for other types
        title: '',
        excerpt: '',
        site_name: '',
        tags_text: '',
        tags: null,
        cover_url: null,
        is_starred: false,
        status: null,
        last_viewed_at: null,
        exact_text: '',
        article_id: null,
      });
    });

    // Dedup by id to prevent MiniSearch "duplicate ID" crash during cleanup races
    const seen = new Set();
    const uniqueDocs = docs.filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });
    index.addAll(uniqueDocs);
    return index;
  }, [articles, highlights, cards]);

  // Search function with pinyin augmentation
  const search = useCallback((query) => {
    if (!query || !query.trim()) return [];

    const trimmedQuery = query.trim();

    // Phase 1: MiniSearch results
    let results;
    try {
      results = searchIndex.search(trimmedQuery, {
        prefix: true,
        fuzzy: 0.2,
        boost: MINISEARCH_OPTIONS.searchOptions.boost,
      });
    } catch {
      results = [];
    }

    // Phase 2: Pinyin augmentation (only for ASCII-like queries, 2+ chars)
    if (isPinyinLikeQuery(trimmedQuery) && trimmedQuery.length >= 2) {
      const resultIds = new Set(results.map(r => r.id));
      const pinyinResults = [];

      // Check articles
      articles.forEach(a => {
        const key = `article:${a.id}`;
        const score = Math.max(
          getPinyinScore(a.title, trimmedQuery),
          getPinyinScore(a.excerpt, trimmedQuery),
          getPinyinScore(a.site_name, trimmedQuery),
        );
        if (score > 0) {
          if (resultIds.has(key)) {
            // Boost existing result
            const existing = results.find(r => r.id === key);
            if (existing) existing.score = Math.max(existing.score, score);
          } else {
            // Add new pinyin-only result
            pinyinResults.push({
              id: key,
              _type: 'article',
              originalId: a.id,
              title: a.title || '',
              excerpt: a.excerpt || '',
              site_name: a.site_name || '',
              tags: a.tags,
              cover_url: a.cover_url,
              is_starred: a.is_starred,
              status: a.status,
              last_viewed_at: a.last_viewed_at,
              created_at: a.created_at,
              score,
            });
          }
        }
      });

      // Check highlights
      highlights.forEach(h => {
        const key = `highlight:${h.id}`;
        const score = getPinyinScore(h.exact_text, trimmedQuery);
        if (score > 0 && !resultIds.has(key)) {
          pinyinResults.push({
            id: key,
            _type: 'highlight',
            originalId: h.id,
            exact_text: h.exact_text || '',
            article_id: h.article_id,
            created_at: h.created_at,
            score,
          });
        }
      });

      // Check cards
      cards.forEach(c => {
        const key = `card:${c.id}`;
        const score = Math.max(
          getPinyinScore(c.front, trimmedQuery),
          getPinyinScore(c.back, trimmedQuery),
        );
        if (score > 0 && !resultIds.has(key)) {
          pinyinResults.push({
            id: key,
            _type: 'card',
            originalId: c.id,
            front: c.front || '',
            back: c.back || '',
            deck_id: c.deck_id,
            source_article_id: c.source_article_id,
            created_at: c.created_at,
            score,
          });
        }
      });

      // Merge and re-sort
      results = [...results, ...pinyinResults].sort((a, b) => b.score - a.score);
    }

    return results;
  }, [searchIndex, articles, highlights, cards]);

  // Get recent articles (for empty query state)
  const getRecentArticles = useCallback((limit = 5) => {
    return [...articles]
      .sort((a, b) => {
        const aDate = new Date(a.last_viewed_at || a.created_at);
        const bDate = new Date(b.last_viewed_at || b.created_at);
        return bDate - aDate;
      })
      .slice(0, limit);
  }, [articles]);

  return {
    search,
    getRecentArticles,
    isReady: true,
  };
};

export default useSearchIndex;
